/**
 * Shared validation/normalization helpers for the secops onboarding wizard.
 * Extracted so the same rules are enforced consistently across the wizard,
 * the search page, and project settings — and so they're independently testable.
 */

/** Mercedes-Benz corporate emails only. */
export const isValidMBEmail = (email: string): boolean =>
  email.trim().toLowerCase().endsWith('@mercedes-benz.com');

/** APP IDs must always be "APP-" followed by exactly 5 digits, e.g. APP-12345. */
export const APP_ID_REGEX = /^APP-\d{5}$/;

export const isValidAppId = (value: string): boolean => APP_ID_REGEX.test(value.trim());

/**
 * Normalizes free-form input into the canonical `APP-NNNNN` shape: strips any existing
 * "APP-" prefix (case-insensitive), keeps only digits, caps at 5, and re-applies the prefix.
 * Always returns at least "APP-" so the prefix can be pre-filled/locked in form fields.
 */
export const normalizeAppId = (raw: string): string =>
  `APP-${raw.toUpperCase().replace(/^APP-?/, '').replace(/\D/g, '').slice(0, 5)}`;

/** RFC-4122 UUID — mirrors the backend `isValidUuid` check in polarisMetadataService.js. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * In a Polaris browser URL the application UUID is the `portfolio-items` segment:
 *   https://<tenant>/portfolio/portfolios/<portfolioId>/portfolio-items/<appId>/projects
 * API-style `/applications/<uuid>` paths are accepted too. Anchoring on the segment
 * name keeps the extraction correct even when deeper URLs carry extra UUIDs.
 */
const POLARIS_APP_URL_REGEX = /(?:portfolio-items|applications)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Extracts a Polaris application UUID from user input — either a bare UUID or a
 * URL pasted straight from the browser. Returns the normalized (lowercase) UUID,
 * or null when nothing parseable is found. Backend stays strict UUID-only; this
 * is the single client-side normalization point used by the onboarding wizard
 * and Project Settings → Connections.
 */
export const parsePolarisApplicationId = (raw: string): string | null => {
  const input = raw.trim();
  if (!input) return null;
  if (UUID_REGEX.test(input)) return input.toLowerCase();
  const match = input.match(POLARIS_APP_URL_REGEX);
  return match ? match[1].toLowerCase() : null;
};
